import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId, IMAGES } from "@/lib/scroll";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgWordY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-between overflow-hidden pt-24 sm:pt-32">
      <motion.div
        style={{ y: bgWordY }}
        aria-hidden
        className="pointer-events-none select-none absolute -bottom-6 left-1/2 -translate-x-1/2 font-display font-bold uppercase text-outline text-[22vw] leading-none tracking-tight whitespace-nowrap"
      >
        SOLAMOVE
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative z-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-mono-alt text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#736F6E] mb-6 sm:mb-8"
          >
            Semelle lestée — En cours de développement — 2026
          </motion.p>

          <h1 className="font-display font-light uppercase text-[#0D0D0D] leading-[0.95] tracking-tight text-[13.5vw] sm:text-7xl lg:text-[5.6rem] xl:text-[6.5rem]">
            <MaskedLine delay={0.35}>Vous marchez</MaskedLine>
            <MaskedLine delay={0.5}>déjà.</MaskedLine>
          </h1>

          <h2 className="font-display uppercase leading-[1.02] tracking-tight text-[7.5vw] sm:text-4xl lg:text-5xl mt-6 sm:mt-8 text-[#0D0D0D]">
            <MaskedLine delay={0.75} className="font-light text-[#6E6A66]">Et si chaque pas</MaskedLine>
            <MaskedLine delay={0.9} className="font-medium">comptait davantage ?</MaskedLine>
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 sm:mt-8 max-w-md text-sm sm:text-lg text-[#6E6A66] leading-relaxed"
          >
            SOLAMOVE est une semelle lestée qui s'intègre à ce que vous faites déjà tous les jours : marcher.
            Rien à bouleverser, aucun temps à trouver.
          </motion.p>

          <motion.p
            data-testid="hero-tagline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-5 sm:mt-6 flex items-center gap-3 font-mono-alt text-[10px] sm:text-sm uppercase tracking-[0.3em] text-[#0D0D0D]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#A8B89A]" />
            Every step makes you stronger.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 sm:mt-10"
          >
            <button
              data-testid="hero-prereserve-cta"
              onClick={() => scrollToId("reserver")}
              className="group relative overflow-hidden bg-[#0D0D0D] text-[#FAF8F5] font-display uppercase tracking-[0.15em] text-sm px-10 py-5 rounded-full w-full sm:w-auto"
            >
              <span className="relative z-10 transition-colors duration-500 group-hover:text-[#0D0D0D]">Pré-réserver gratuitement</span>
              <span className="absolute inset-0 bg-[#E6CECA] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            </button>
            <p className="mt-5 font-mono-alt text-[11px] uppercase tracking-[0.2em] text-[#736F6E]">
              Gratuit • Sans engagement • Accès prioritaire au lancement
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="flex -space-x-2">
                {["#F3ECE3", "#CBD5C0", "#E6CECA", "#1A1918"].map((hex) => (
                  <span key={hex} className="h-6 w-6 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: hex }} />
                ))}
              </div>
              <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#736F6E]">4 coloris en étude</span>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[3/4] overflow-hidden"
          >
            <motion.img
              src={IMAGES.hero}
              alt="SOLAMOVE — semelle lestée portée au quotidien, univers urbain premium"
              style={{ y: imgY, scale: 1.15 }}
              className="w-full h-full object-cover"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="mt-4 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#736F6E] flex justify-between"
          >
            <span>N°001 — Objet design</span>
            <span>Paris, FR</span>
          </motion.p>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("concept")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="relative z-10 mx-auto mb-10 mt-14 flex flex-col items-center gap-3 text-[#736F6E] hover:text-[#0D0D0D] transition-colors"
      >
        <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em]">Défiler</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1.5} />
        </motion.span>
      </motion.button>
    </section>
  );
};
